import { lazy, Suspense, useEffect, useState } from 'react'
import { Outlet, useLoaderData, useLocation, useMatches } from 'react-router'
import Footer from './components/Footer'
import Header from './components/Header'
import { initAnalytics, ometriaPageType, trackPageView } from './lib/analytics'
import { POPUP_DELAY_MS, popupWanted } from './lib/newsletter'
import { loadSiteData, SiteDataProvider } from './lib/siteData'

// Its own chunk, fetched only when the popup is due: nothing extra for the page to load or run before then.
const EmailPopup = lazy(() => import('./components/EmailPopup'))

/** Route `handle` options understood by the site layout. */
export interface SiteRouteHandle {
  hideFooter?: boolean
}

export async function loader() {
  return { site: await loadSiteData() }
}

/** The menu doesn't depend on the page: load it with the first page, not on every navigation. */
export const shouldRevalidate = () => false

/** Shell for every customer-facing page: header + menu, page content, footer. */
export default function SiteLayout() {
  const { site } = useLoaderData<typeof loader>()
  const location = useLocation()
  const matches = useMatches()
  const hideFooter = matches.some((m) => (m.handle as SiteRouteHandle | undefined)?.hideFooter)
  const pageType = ometriaPageType(matches[matches.length - 1]?.id)

  useEffect(() => {
    initAnalytics()
  }, [])

  // The email popup, POPUP_DELAY_MS after the page has finished loading (not on the next pages once seen).
  const [popupOpen, setPopupOpen] = useState(false)
  useEffect(() => {
    if (!popupWanted()) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const start = () => (timer = setTimeout(() => setPopupOpen(true), POPUP_DELAY_MS))
    if (document.readyState === 'complete') start()
    else window.addEventListener('load', start, { once: true })
    return () => {
      window.removeEventListener('load', start)
      clearTimeout(timer)
    }
  }, [])

  // GA4 page_view and Ometria page type per client-side navigation (no-ops unless enabled in the runtime config).
  useEffect(() => {
    trackPageView(location.pathname + location.search, pageType)
  }, [location.pathname, location.search, pageType])

  return (
    <SiteDataProvider value={site}>
      <div className="qt-site flex min-h-screen flex-col">
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-xl focus:bg-white focus:px-4 focus:py-2 focus:font-bold">
          Skip to content
        </a>
        <Header />
        <main id="main" className="flex-1">
          <Outlet />
        </main>
        {!hideFooter && <Footer />}
        {popupOpen && (
          <Suspense fallback={null}>
            <EmailPopup onClose={() => setPopupOpen(false)} />
          </Suspense>
        )}
      </div>
    </SiteDataProvider>
  )
}
